/**
 * 순회방문 일자 관리 다이얼로그
 *
 * 순회방문(연 2회) 기간을 등록·삭제한다. 최신 종료일 + 1일이
 * S-13 조회 시작일 기본값으로 자동 지정된다. 기간(시작~종료)은
 * 참고용 보관이며 실사용은 종료일뿐이다.
 * 데이터 호출은 S13Page가 담당하고 이 컴포넌트는 표시·입력만 한다.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Stack,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import { CircuitVisit } from './s13-types';

interface S13CircuitVisitDialogProps {
  open: boolean;
  visits: CircuitVisit[];
  busy: boolean;
  onClose: () => void;
  /** 등록 성공 시 true 반환 (입력 폼 초기화용) */
  onAdd: (startDate: string, endDate: string) => Promise<boolean>;
  onDelete: (id: number) => void;
}

const S13CircuitVisitDialog: React.FC<S13CircuitVisitDialogProps> = ({
  open,
  visits,
  busy,
  onClose,
  onAdd,
  onDelete,
}) => {
  const [newStart, setNewStart] = useState<Dayjs | null>(null);
  const [newEnd, setNewEnd] = useState<Dayjs | null>(null);

  const validInput =
    newStart !== null &&
    newStart.isValid() &&
    newEnd !== null &&
    newEnd.isValid() &&
    !newStart.isAfter(newEnd);

  const handleAdd = async () => {
    if (!validInput || !newStart || !newEnd) return;
    const ok = await onAdd(
      newStart.format('YYYY-MM-DD'),
      newEnd.format('YYYY-MM-DD'),
    );
    if (ok) {
      setNewStart(null);
      setNewEnd(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>순회방문 일자 관리</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          최신 종료일의 다음 날이 S-13 조회 시작일로 자동 지정됩니다. 기간은
          참고용으로 보관됩니다.
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
          >
            <DatePicker
              label="방문 시작일"
              value={newStart}
              onChange={setNewStart}
              disabled={busy}
              inputFormat="YYYY-MM-DD"
              renderInput={(params) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <TextField {...params} size="small" />
              )}
            />
            <DatePicker
              label="방문 종료일"
              value={newEnd}
              onChange={setNewEnd}
              disabled={busy}
              inputFormat="YYYY-MM-DD"
              renderInput={(params) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <TextField {...params} size="small" />
              )}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={busy || !validInput}
            >
              등록
            </Button>
          </Stack>
        </LocalizationProvider>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>방문 기간</TableCell>
              <TableCell align="right">삭제</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visits.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary">
                    등록된 순회방문 기록이 없습니다.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {visits.map((visit, index) => (
              <TableRow key={visit.id}>
                <TableCell>
                  {visit.startDate} ~ {visit.endDate}
                  {index === 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="primary"
                      sx={{ ml: 1 }}
                    >
                      최신 — 조회 시작일 {dayjs(visit.endDate)
                        .add(1, 'day')
                        .format('YYYY-MM-DD')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label="순회방문 기록 삭제"
                    size="small"
                    onClick={() => onDelete(visit.id)}
                    disabled={busy}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default S13CircuitVisitDialog;
